import React, { useState } from 'react';
import ReactSelect from 'react-select';
import type { ShippingPlan_ShipFragment } from '../../graphql';
import { useVesselChangeFormQuery } from '../../graphql';
import { nodesToOptions, reactSelectStyles } from '../../lib/form-util';
import { isDefined } from '../../lib/util';
import { Button, FormField, Box, FormRow, Text, Input } from '../ui';

export type vesselFilterProps = {
  fromDate: Date | null;
  toDate: Date | null;
};

export interface VesselChangeFormProps {
  coaId: number | null;
  onVesselChange: (vesselId: number) => void;
  shipownerOrgId: number | null;
  vessel: ShippingPlan_ShipFragment | null;
  voyageCharterId: number | null;
}

const VesselChangeForm = ({
  coaId,
  onVesselChange,
  shipownerOrgId,
  vessel,
  voyageCharterId,
}: VesselChangeFormProps): JSX.Element => {
  const { data } = useVesselChangeFormQuery({
    variables: {
      coaId,
      includeCoaVessels: coaId !== null,
      shipownerOrgId: shipownerOrgId ?? -1,
      includeVoyageCharter: voyageCharterId !== null,
      voyageCharterId: voyageCharterId ?? -1,
    },
    skip: shipownerOrgId === null,
  });

  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [selectedVessel, setSelectedVessel] = useState(
    vessel ? { value: vessel.id, label: vessel.name } : undefined,
  );

  const vessels = data?.allShips?.nodes.filter(isDefined) ?? [];
  const coaVesselIds =
    data?.allCoaContractShips?.nodes
      .filter(isDefined)
      .map((coaVessel) => coaVessel.shipId) ?? null;
  const vesselOptions = nodesToOptions(
    coaVesselIds
      ? vessels.filter((vessel) => coaVesselIds.includes(vessel.id))
      : vessels,
  );

  const hasChanged = selectedVessel?.value !== vessel?.id;

  const nominatedVessel = data?.voyageCharterById?.shipByVesselId?.name ?? null;

  const onSave = () => {
    const newVesselId = selectedVessel?.value ?? null;

    if (!newVesselId) return null;

    setSaveLoading(true);

    try {
      onVesselChange(newVesselId);
      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving changed vessel', err);
      setSaveError(true);
    }

    setSaveLoading(false);
  };

  return (
    <Box
      p={3}
      pb={2}
      mb={4}
      bg="neutral.white_100"
      borderColor="neutral.gray_225"
      borderWidth={1}
      borderStyle="solid"
    >
      <FormRow>
        <FormField
          label="Change vessel"
          width={200}
          data-testid="change-vessel"
        >
          <ReactSelect
            styles={reactSelectStyles}
            options={vesselOptions}
            value={selectedVessel}
            onChange={setSelectedVessel}
          />
        </FormField>
        {voyageCharterId !== null ? (
          <FormField
            label="Nominated vessel"
            width={200}
            data-testid="nominated-vessel"
          >
            <Input disabled value={nominatedVessel ?? ''} />
          </FormField>
        ) : null}
        <FormField width={300}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Button
              variant="secondary"
              disabled={!hasChanged || saveLoading}
              onPress={onSave}
              mr={3}
            >
              Save
            </Button>
            {saveSuccess && (
              <Text fontWeight="bold" color="#5fad09">
                ✓ Saved successfully
              </Text>
            )}
            {saveError && (
              <Text fontWeight="bold" color="#FC4A15">
                ✗ Error saving
              </Text>
            )}
          </Box>
        </FormField>
      </FormRow>
    </Box>
  );
};

export default VesselChangeForm;
